import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Users, Hammer, Award } from "lucide-react";
import { COMPANY, STATS } from "../data/content";

const ABOUT_IMG_1 =
  "https://images.unsplash.com/photo-1646324554833-f0b6a479fa5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const ABOUT_IMG_2 =
  "https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Honest Diagnosis",
    text: "We tell you what your home actually needs — not what gets us a bigger ticket.",
  },
  {
    icon: Hammer,
    title: "Built To Last",
    text: "We use the materials we'd put on our own homes. We've used them — for 30 years.",
  },
  {
    icon: Users,
    title: "Local Crews",
    text: "Our crews live in the same towns we work in. You'll see us at the hardware store.",
  },
  {
    icon: Award,
    title: "Manufacturer Certified",
    text: "GAF, CertainTeed and James Hardie certified — meaning warranties that actually mean something.",
  },
];

const TIMELINE = [
  { year: "1994", title: "Founded on the South Shore", text: "Started as a two-man roofing crew working on capes and triple-deckers across the South Shore." },
  { year: "2002", title: "Expanded into Siding & Gutters", text: "Brought on a dedicated siding & seamless-gutter team to handle full home exteriors alongside our roofing work." },
  { year: "2011", title: "Home Improvement Division", text: "Added kitchen & bathroom remodeling to serve homeowners who already trusted us with their roofs." },
  { year: "2024", title: "Relocated to Braintree — 1,000+ Roofs Completed", text: "Moved into our new headquarters at 36 Central Ave, Braintree — same family ownership, same workmanship guarantee on every roof." },
];

export default function About() {
  return (
    <div data-testid="page-about">
      {/* Header */}
      <section className="bg-[var(--navy)] text-white pt-36 pb-24 md:pb-32 relative overflow-hidden">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="eyebrow eyebrow-line mb-4">About Admiral</div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-[5rem] leading-[1.02] tracking-tight">
              Built on {COMPANY.yearsInBusiness} Years of
              <br />
              <span className="italic font-light text-white/80">Braintree Craftsmanship.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              A family-run roofing &amp; home improvement company that has spent three decades doing one thing — taking care of New England homes the way we'd want ours taken care of.
            </p>
          </div>
        </div>
      </section>

      {/* Editorial split */}
      <section className="bg-[var(--bg-light)] py-20 md:py-28">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-6 relative">
            <img
              src={ABOUT_IMG_1}
              alt="Admiral crew on roof"
              className="w-full h-[460px] md:h-[560px] object-cover"
            />
            <img
              src={ABOUT_IMG_2}
              alt="Admiral framing work"
              className="hidden md:block absolute -bottom-12 -right-12 w-[240px] h-[300px] object-cover border-8 border-[var(--bg-light)]"
            />
          </div>
          <div className="md:col-span-6 md:pl-8">
            <div className="eyebrow mb-3">Our Story</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--navy)] leading-[1.05] mb-8">
              We started in 1994. Same family. Same town. Same answer when the phone rings.
            </h2>
            <div className="space-y-5 text-[var(--text-mute)] text-base md:text-lg leading-relaxed">
              <p>
                Admiral Roofing was founded in {COMPANY.founded} as a small
                South Shore roofing crew. What started as a two-person outfit
                driving a single pickup has grown into a full roofing &amp; home
                improvement company — now headquartered at 36 Central Ave in
                Braintree — but the rules haven't changed.
              </p>
              <p>
                We still answer our own phones. We still walk every roof before
                we quote it. And we still believe a New England roof should
                outlast the people who paid for it.
              </p>
              <p>
                Whether it's a same-week shingle repair in Braintree or a full
                tear-off and replacement in Weymouth, you get the same crew, the
                same standards, and the same name on the truck.
              </p>
            </div>
            <Link to="/contact" data-testid="about-cta-estimate" className="btn-copper mt-10">
              Talk To Us <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-y border-[var(--border-light)]">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--border-light)]">
          {STATS.map((s) => (
            <div key={s.label} className="py-10 px-6 text-center" data-testid={`about-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="font-serif-display text-4xl md:text-5xl text-[var(--navy)]">
                {s.value}
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--copper)] mt-2">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="bg-[var(--bg-light)] py-20 md:py-32" data-testid="about-values">
        <div className="container-x">
          <div className="max-w-3xl mb-16">
            <div className="eyebrow eyebrow-line mb-3">How We Work</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--navy)] leading-[1.05]">
              Four things we will not compromise on.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-light)] border border-[var(--border-light)]">
            {VALUES.map((v) => (
              <div
                key={v.title}
                className="bg-white p-8 md:p-10 service-card"
                data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <v.icon className="text-[var(--copper)] mb-6" size={28} />
                <h3 className="font-serif-display text-2xl text-[var(--navy)] mb-3">{v.title}</h3>
                <p className="text-sm text-[var(--text-mute)] leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-white py-20 md:py-32" data-testid="about-timeline">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow eyebrow-line mb-3">The Long View</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--navy)] leading-[1.05] sticky top-32">
              Three decades, in four moments.
            </h2>
          </div>
          <div className="md:col-span-8">
            <ol className="relative border-l border-[var(--border-light)] ml-2">
              {TIMELINE.map((t) => (
                <li key={t.year} className="ml-8 pb-12 last:pb-0 relative">
                  <span className="absolute -left-[42px] top-1 w-3 h-3 bg-[var(--copper)] border-4 border-white" />
                  <div className="font-serif-display text-3xl text-[var(--navy)] mb-2">{t.year}</div>
                  <div className="eyebrow mb-2 text-[var(--navy)]">{t.title}</div>
                  <p className="text-[var(--text-mute)] leading-relaxed text-base max-w-xl">{t.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--navy)] text-white py-20 md:py-28">
        <div className="container-x text-center max-w-3xl mx-auto">
          <div className="eyebrow eyebrow-line text-[var(--copper)] mb-4">Ready When You Are</div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-10">
            Whether you've got a leak today or a project for next spring — let's talk.
          </h2>
          <Link to="/contact" data-testid="about-bottom-cta" className="btn-copper">
            Get a Free Estimate <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
